import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Bot,
  Cpu,
  Factory,
  Activity,
  Layers,
  Network,
  ShieldCheck,
  Zap,
  Database,
  Server,
  Atom,
  Users,
  TrendingUp,
  Sparkles,
  Compass,
  Target,
  CheckCircle2,
  LineChart,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";

const TECHNOLOGIES = [
  { icon: Bot, label: "Autonome Roboter", text: "Mobile und stationäre Robotik für flexible Fertigungs- und Logistikprozesse." },
  { icon: Factory, label: "Intelligente Produktionssysteme", text: "Vernetzte Anlagen mit Echtzeitsteuerung und kontinuierlicher Optimierung." },
  { icon: Activity, label: "KI-gestützte Qualitätskontrolle", text: "Computer Vision und ML-Modelle für Null-Fehler-Produktion." },
  { icon: Layers, label: "Digitale Zwillinge", text: "Virtuelle Abbilder von Anlagen, Produkten und Prozessen für Simulation und Optimierung." },
  { icon: Network, label: "Autonome Logistik", text: "AGVs, AMRs und selbststeuernde Materialflüsse entlang der Wertschöpfungskette." },
  { icon: Sparkles, label: "Agentensysteme & GenAI", text: "Multi-Agent-Workflows, die Entscheidungen vorbereiten und Prozesse selbst koordinieren." },
  { icon: Cpu, label: "Selbstoptimierende Fabriken", text: "Geschlossene Regelkreise: Produktion lernt aus jedem Zyklus und passt sich an." },
  { icon: Compass, label: "Predictive Operations", text: "Vorhersage von Ausfällen, Energieverbräuchen und Engpässen vor ihrem Eintreten." },
];

const INFRA_PILLARS = [
  { icon: Zap, label: "Energie", text: "Verfügbarkeit und Sicherung langfristiger, planbarer Stromquellen." },
  { icon: Server, label: "Rechenleistung", text: "Zugang zu KI-Rechenzentren, GPUs und industrieller Edge-Compute." },
  { icon: Atom, label: "Rohstoffe & Halbleiter", text: "Strategische Materialien und Chips als Engpassressourcen der Industrie." },
  { icon: Database, label: "Dateninfrastruktur", text: "Hochwertige, kuratierte Daten als Treibstoff industrieller KI-Modelle." },
  { icon: Users, label: "Talent & Kompetenzen", text: "Spezialisten an der Schnittstelle von Engineering, Daten und KI." },
  { icon: ShieldCheck, label: "Resiliente Lieferketten", text: "Geopolitische Absicherung kritischer Komponenten und Technologien." },
];

const X10_SUPPORT = [
  {
    icon: Target,
    title: "Strategische Einordnung",
    text: "Wir helfen, technologische Entwicklungen einzuordnen und die für Ihr Geschäftsmodell relevanten Hebel zu identifizieren.",
  },
  {
    icon: LineChart,
    title: "Use-Case-Identifikation",
    text: "Systematische Bewertung konkreter Anwendungsfälle entlang Ihrer Wertschöpfungskette – mit klarer Priorisierung.",
  },
  {
    icon: Factory,
    title: "Automatisierung & Robotik",
    text: "Begleitung bei der Auswahl, Pilotierung und Einführung von Robotik-, KI- und Automatisierungslösungen.",
  },
  {
    icon: Network,
    title: "Skalierung & Integration",
    text: "Von der ersten Analyse bis zur produktiven Integration in bestehende Wertschöpfungsketten und Geschäftsmodelle.",
  },
];

const STAT_CARDS = [
  {
    value: "≈ 7 Bio. $",
    label: "Investitionen in KI-Rechenzentren bis 2030",
    source: "McKinsey & Company",
  },
  {
    value: "Mensch + KI + Roboter",
    label: "Neue Produktivitätsformel der Arbeitswelt",
    source: "McKinsey & Company",
  },
  {
    value: "Geopolitisch",
    label: "Wettlauf um Energie, Chips und Rohstoffe",
    source: "McKinsey & Company",
  },
];

export default function IndustrialAI() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="industrial-ai-page" className="font-body bg-white text-slate-900 min-h-screen">
      <SEO
        title="Industrial AI & Robotics | X10 Management"
        description="Industrial AI, Robotik und Automatisierung definieren die nächste Stufe industrieller Wertschöpfung neu. X10 begleitet Unternehmen bei Identifikation, Bewertung und Umsetzung konkreter Anwendungsfälle – von der strategischen Einordnung bis zur Skalierung."
      />
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0A192F] text-white pt-32 lg:pt-36">
        <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-[#C9A96E]/15 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 w-[560px] h-[560px] rounded-full bg-[#1E3A8A]/40 blur-3xl pointer-events-none" />
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Link
              to="/#services"
              data-testid="iai-back"
              className="inline-flex w-fit items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors mb-8"
            >
              <ArrowLeft size={16} /> Zurück zu Leistungen
            </Link>
            <div>
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-extrabold text-[#C9A96E]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#C9A96E]" />
                Discipline 04 · Industrial AI &amp; Robotics
              </span>
            </div>
            <h1 className="mt-5 font-heading font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-4xl">
              Die nächste industrielle Revolution
              <span className="block text-[#C9A96E] mt-2">wird an der Schnittstelle von KI, Robotik und Infrastruktur entschieden.</span>
            </h1>
            <p className="mt-6 text-lg lg:text-xl text-white/85 font-light leading-relaxed max-w-3xl">
              Nicht Software oder Daten allein entscheiden – sondern das Zusammenspiel aus Künstlicher Intelligenz,
              Robotik, Automatisierung, Energieversorgung und industrieller Infrastruktur. Wer heute richtig
              entscheidet, gewinnt morgen.
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/#contact"
                data-testid="iai-cta-contact"
                className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white text-[#0A192F] font-semibold hover:bg-[#C9A96E] hover:text-white transition-all duration-300"
              >
                Use-Case-Analyse anfragen
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href="#x10-support"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/30 font-semibold hover:bg-white/10 transition-all duration-300"
              >
                Wie X10 begleitet
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden ring-1 ring-white/10 max-w-md ml-auto">
              <img
                src="https://images.unsplash.com/photo-1581090464777-f3220bbe1b8b?auto=format&fit=crop&w=1200&q=80"
                alt="Industrielle Robotik und KI-gestützte Fertigung"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0A192F]/80 via-[#0A192F]/30 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9A96E] font-semibold">
                  Strategic Field
                </div>
                <div className="font-heading text-lg font-semibold mt-1 leading-tight">
                  Industrial AI &amp; Robotics
                </div>
                <div className="text-sm text-white/80 mt-1">
                  KI · Robotik · Automation · Infrastruktur
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-sm uppercase tracking-[0.25em] font-extrabold text-[#1E3A8A]">Status der Technologien</span>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold text-[#0A192F] tracking-tight leading-tight">
            Vieles ist heute schon technisch realisierbar.
          </h2>
          <div className="mt-8 space-y-6 text-lg text-slate-700 leading-relaxed">
            <p>
              Während viele Unternehmen noch über KI diskutieren, sind zahlreiche Technologien bereits marktreif.
              Autonome Roboter, intelligente Produktionssysteme, KI-gestützte Qualitätskontrolle, digitale Zwillinge,
              autonome Logistik, Agentensysteme und selbstoptimierende Fabriken sind <span className="text-[#0A192F] font-medium">technisch realisierbar</span>.
            </p>
            <p>
              Die eigentliche Herausforderung liegt nicht mehr in der Technologie selbst – sondern in ihrer
              Skalierung, Integration und Verfügbarkeit innerhalb realer Wertschöpfungsketten.
            </p>
          </div>
        </div>
      </section>

      {/* Technologies Grid */}
      <section className="bg-[#F3F4F6] py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-sm uppercase tracking-[0.25em] font-extrabold text-[#1E3A8A]">
              Technologie-Landkarte
            </span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold text-[#0A192F] tracking-tight leading-tight">
              Acht Bausteine der industriellen Zukunft.
            </h2>
            <p className="mt-5 text-lg text-slate-700 leading-relaxed">
              Diese Technologien sind heute keine Vision mehr, sondern bereits einsatzfähige Bausteine moderner
              Produktion, Logistik und Wertschöpfung:
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {TECHNOLOGIES.map(({ icon: Icon, label, text }, i) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="group p-6 rounded-2xl bg-white border border-slate-200 hover:border-[#C9A96E]/60 hover:shadow-[0_0_24px_rgba(201,169,110,0.15)] transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-full bg-[#0A192F]/5 border border-[#C9A96E]/40 flex items-center justify-center">
                  <Icon size={18} className="text-[#0A192F]" />
                </div>
                <div className="mt-4 font-heading text-base font-semibold text-[#0A192F]">{label}</div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Future of Work / Stats */}
      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <span className="text-sm uppercase tracking-[0.25em] font-extrabold text-[#1E3A8A]">
                Zukunft der Arbeit
              </span>
              <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold text-[#0A192F] tracking-tight leading-tight">
                Mensch, KI-Agent und Roboter <span className="text-[#1E3A8A]">– die neue Produktivitätsformel.</span>
              </h2>
              <p className="mt-6 text-lg text-slate-700 leading-relaxed">
                Internationale Studien zeigen: Unternehmen werden ihre Produktivität künftig nicht mehr allein durch
                zusätzliche Mitarbeiter steigern, sondern durch intelligente Partnerschaften zwischen Mensch und
                Maschine. Wer dieses Zusammenspiel früh beherrscht, gewinnt strukturelle Vorteile.
              </p>
              <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0A192F]/5 border border-[#C9A96E]/30">
                <Sparkles size={14} className="text-[#C9A96E]" />
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0A192F]">
                  Quelle: McKinsey &amp; Company
                </span>
              </div>
            </div>

            <div className="lg:col-span-7 grid sm:grid-cols-3 gap-4">
              {STAT_CARDS.map(({ value, label, source }, i) => (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="p-6 rounded-2xl bg-[#0A192F] text-white border border-[#0A192F] hover:border-[#3B82F6]/40 transition-all duration-300 flex flex-col"
                >
                  <TrendingUp size={18} className="text-[#C9A96E]" />
                  <div className="mt-5 font-heading text-2xl lg:text-3xl font-bold tracking-tight leading-tight">
                    {value}
                  </div>
                  <p className="mt-3 text-sm text-slate-300 leading-relaxed">{label}</p>
                  <div className="mt-auto pt-4 text-[10px] uppercase tracking-[0.25em] font-extrabold text-[#C9A96E]">
                    {source}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Infrastructure Race */}
      <section className="bg-[#0A192F] text-white py-16 lg:py-24 relative overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[440px] h-[440px] rounded-full bg-[#C9A96E]/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 w-[440px] h-[440px] rounded-full bg-[#1E3A8A]/30 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-extrabold text-[#C9A96E]">
              <Atom size={14} /> Infrastrukturwettlauf
            </span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
              Ein Wettlauf historischen Ausmaßes <span className="text-[#C9A96E]">um die Ressourcen der Zukunft.</span>
            </h2>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed">
              Wer industrielle Zukunft gestalten möchte, braucht Zugang zu Energie, Rechenleistung, kritischen
              Rohstoffen, Halbleitern, Dateninfrastruktur und hochqualifizierten Fachkräften. USA, China und andere
              Wirtschaftsräume investieren parallel Milliardenbeträge in Automatisierung, Robotik und KI-Kapazitäten.
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {INFRA_PILLARS.map(({ icon: Icon, label, text }, i) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="p-6 rounded-2xl bg-white/5 border border-white/10 hover:border-[#C9A96E]/40 transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-full bg-[#C9A96E]/10 border border-[#C9A96E]/30 flex items-center justify-center">
                  <Icon size={18} className="text-[#C9A96E]" />
                </div>
                <div className="mt-4 font-heading text-lg font-semibold">{label}</div>
                <p className="mt-2 text-sm text-slate-300 leading-relaxed">{text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* X10 Support */}
      <section id="x10-support" className="bg-white py-16 lg:py-24 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-sm uppercase tracking-[0.25em] font-extrabold text-[#1E3A8A]">Unser Beitrag</span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold text-[#0A192F] tracking-tight leading-tight">
              Wie X10 Unternehmen begleitet.
            </h2>
            <p className="mt-6 text-lg text-slate-700 leading-relaxed">
              Wir unterstützen bei der Identifikation, Bewertung und Umsetzung konkreter Anwendungsfälle entlang der
              gesamten Wertschöpfungskette – von der strategischen Einordnung über die Automatisierung von Prozessen
              bis zur Einführung intelligenter Robotik- und KI-Lösungen.
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 gap-4">
            {X10_SUPPORT.map(({ icon: Icon, title, text }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="p-6 rounded-2xl bg-[#0A192F] text-white border border-[#0A192F] hover:border-[#3B82F6]/50 hover:shadow-[0_0_30px_rgba(59,130,246,0.2)] transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-full bg-white/10 border border-[#C9A96E]/30 flex items-center justify-center">
                  <Icon size={18} className="text-[#C9A96E]" />
                </div>
                <div className="mt-4 font-heading text-lg font-semibold">{title}</div>
                <p className="mt-2 text-sm text-slate-300 leading-relaxed">{text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom Line */}
      <section className="bg-[#F3F4F6] py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative p-8 lg:p-12 rounded-3xl bg-white border border-slate-200 shadow-sm">
            <div className="absolute -top-4 left-8 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#0A192F] text-[10px] uppercase tracking-[0.3em] font-extrabold text-[#C9A96E]">
              Bottom Line
            </div>
            <h3 className="mt-2 font-heading text-2xl sm:text-3xl font-bold text-[#0A192F] tracking-tight leading-tight">
              Nicht "ob", sondern "wie schnell".{" "}
              <span className="text-[#1E3A8A]">Die Frage ist die Integrationsgeschwindigkeit.</span>
            </h3>
            <div className="mt-6 space-y-4 text-slate-700 leading-relaxed">
              <p>
                Die Unternehmen, die heute die richtigen technologischen und strategischen Entscheidungen treffen,
                werden morgen nicht nur effizienter arbeiten – sie werden die Gewinner der nächsten industriellen
                Entwicklungsstufe sein.
              </p>
              <p>
                <CheckCircle2 size={16} className="inline mr-2 -mt-0.5 text-[#C9A96E]" />
                Produktivität, Resilienz und Wettbewerbsfähigkeit entstehen dort, wo Industrial AI &amp; Robotics
                konsequent in Wertschöpfung übersetzt werden.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A192F] text-white py-16 lg:py-20 relative overflow-hidden">
        <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#1E3A8A]/40 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -right-32 w-[420px] h-[420px] rounded-full bg-[#C9A96E]/15 blur-3xl pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-extrabold text-[#C9A96E]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#C9A96E]" /> Get in touch
          </span>
          <h3 className="mt-5 font-heading text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
            Sprechen Sie mit uns über die Chancen von Industrial AI &amp; Robotics.
          </h3>
          <p className="mt-5 text-lg text-slate-300 leading-relaxed">
            Entwickeln Sie gemeinsam mit X10 die Produktions-, Logistik- und Geschäftsmodelle der Zukunft – von der
            Use-Case-Identifikation bis zur produktiven Skalierung.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link
              to="/#contact"
              data-testid="iai-cta-bottom"
              className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white text-[#0A192F] font-semibold hover:bg-[#C9A96E] hover:text-white transition-all duration-300"
            >
              Beratungsgespräch vereinbaren
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/#services"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/30 font-semibold hover:bg-white/10 transition-all duration-300"
            >
              Weitere Leistungen entdecken
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
